import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Zap, ShieldCheck, Medal } from 'lucide-react';
import { useLeaderboard } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import { LeaderboardEntry } from '@/types';
import SEO from '@/components/SEO';

type Tab = 'levels' | 'reputation' | 'trophies';

const TAB_CONFIG: Record<Tab, { label: string; icon: typeof Trophy; unit: string; color: string }> = {
  levels:     { label: 'Top Collectors', icon: Zap,         unit: 'XP',         color: 'text-brand-600 dark:text-brand-400' },
  reputation: { label: 'Most Trusted',   icon: ShieldCheck, unit: 'reputation', color: 'text-emerald-600' },
  trophies:   { label: 'Trophy Leaders', icon: Trophy,      unit: 'trophies',   color: 'text-amber-600' },
};

const MEDAL_COLORS = ['text-yellow-500', 'text-slate-400', 'text-amber-700 dark:text-amber-300'];

function RankBadge({ rank }: { rank: number }) {
  if (rank <= 3) {
    return <Medal className={`h-5 w-5 ${MEDAL_COLORS[rank - 1]}`} />;
  }
  return <span className="text-sm font-bold text-gray-500 dark:text-zinc-500 w-5 text-center">{rank}</span>;
}

function LeaderboardRow({ entry, unit, isMe }: { entry: LeaderboardEntry; unit: string; isMe: boolean }) {
  return (
    <Link
      to={`/profile/${entry.user_id}`}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 ${isMe ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-100' : ''}`}
    >
      <div className="w-6 flex items-center justify-center flex-shrink-0">
        <RankBadge rank={entry.rank} />
      </div>

      <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-zinc-700 overflow-hidden flex-shrink-0">
        {entry.avatar_url
          ? <img src={entry.avatar_url} alt={entry.username} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-500 dark:text-zinc-500">
              {entry.username[0]?.toUpperCase()}
            </div>
        }
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 dark:text-zinc-100 truncate text-sm">
          {entry.username}
          {isMe && <span className="ml-2 text-xs text-brand-600 dark:text-brand-400 font-medium">(You)</span>}
        </p>
      </div>

      <div className="text-right flex-shrink-0">
        <p className="font-bold text-gray-900 dark:text-zinc-100 text-sm">{entry.value.toLocaleString()}</p>
        <p className="text-xs text-gray-500 dark:text-zinc-500">{unit}</p>
      </div>
    </Link>
  );
}

export default function LeaderboardPage() {
  const [tab, setTab] = useState<Tab>('levels');
  const currentUser = useAuthStore((s) => s.user);

  const { data: entries = [], isLoading } = useLeaderboard(tab, 50);
  const config = TAB_CONFIG[tab];

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        url="/leaderboards"
        description="See the top Open Card Vault collectors ranked by XP, reputation, and trophies. Who's the best trader in the community?"
      />
      <div className="container-custom py-8 max-w-2xl">
        <div className="mb-6">
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-zinc-100">Leaderboards</h1>
          <p className="text-gray-500 dark:text-zinc-500 mt-1">Top traders, collectors, and deck builders.</p>
        </div>

        {/* Tabs */}
        <div className="flex bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-1 gap-1 mb-6">
          {(Object.keys(TAB_CONFIG) as Tab[]).map(t => {
            const c = TAB_CONFIG[t];
            const Icon = c.icon;
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === t ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
            <config.icon className={`h-4 w-4 ${config.color}`} />
            <p className="font-semibold text-gray-800 dark:text-zinc-200 text-sm">{config.label}</p>
          </div>

          {isLoading ? (
            <div className="divide-y divide-gray-50">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className="skeleton w-6 h-6 rounded" />
                  <div className="skeleton w-9 h-9 rounded-full" />
                  <div className="skeleton flex-1 h-4 rounded" />
                  <div className="skeleton w-16 h-4 rounded" />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-zinc-500 text-sm">
              No data yet — be the first to climb the ranks!
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {entries.map(entry => (
                <LeaderboardRow
                  key={entry.user_id}
                  entry={entry}
                  unit={config.unit}
                  isMe={entry.user_id === currentUser?.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
